import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Search, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import {
  MONITOR_SEVERITIES,
  severityLabel,
  useServerOptions,
  type AlertChannel,
  type AlertRecipient,
  type MonitoringProjectRef,
  type MonitorSeverity,
} from '../lib/monitoring';
import { FormGrid, FormPage } from '../components/FormPage';
import { Button, Checkbox, ConfirmDialog, Field, Input, Select, useToast } from '../components/ui';

interface TelegramChat {
  id: string;
  type: string;
  title: string;
}

/**
 * Routes: `/monitoring/alerts/new` and `/monitoring/alerts/:id/edit`. A recipient
 * is an email address or a Telegram chat, from an importance up, about the
 * servers or projects picked, or all of either (operator, 2026-10-20: "add
 * option all").
 *
 * "All" is every one, one added later too — not today's list ticked. "All
 * servers" is every URL, kept as no server and no project picked; "All
 * projects" is the recipient's `allProjects`. Nothing picked at all cannot be
 * saved: kept, it would read as everything.
 */
export function MonitoringRecipientFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const servers = useServerOptions();
  const projects = useQuery({
    queryKey: ['monitoring', 'project-options'],
    queryFn: () => api.get<MonitoringProjectRef[]>('/monitoring/project-options'),
  });
  const existing = useQuery({
    queryKey: ['monitoring', 'recipient', id],
    queryFn: () => api.get<AlertRecipient>(`/monitoring/recipients/${id}`),
    enabled: editing,
  });

  const [form, setForm] = useState({
    name: '',
    channel: 'TELEGRAM' as AlertChannel,
    target: '',
    locale: 'en' as 'en' | 'ar',
    minSeverity: 'LOW' as MonitorSeverity,
    notifyRecovery: true,
    isActive: true,
  });
  const [serverIds, setServerIds] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  // A new recipient hears about everything, as before.
  const [allServers, setAllServers] = useState(true);
  const [allProjects, setAllProjects] = useState(false);
  const [chats, setChats] = useState<TelegramChat[] | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    const r = existing.data;
    setForm({
      name: r.name,
      channel: r.channel,
      target: r.target,
      locale: r.locale,
      minSeverity: r.minSeverity,
      notifyRecovery: r.notifyRecovery,
      isActive: r.isActive,
    });
    setServerIds(r.serverIds);
    setProjectIds(r.projectIds);
    setAllProjects(r.allProjects);
    setAllServers(r.serverIds.length === 0 && r.projectIds.length === 0 && !r.allProjects);
  }

  const toggle = (list: string[], set: (next: string[]) => void, value: string) =>
    set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  const coversNothing = !allServers && !allProjects && serverIds.length === 0 && projectIds.length === 0;

  const findChats = useMutation({
    mutationFn: () => api.get<TelegramChat[]>('/monitoring/telegram/chats'),
    onSuccess: (found) => setChats(found),
    onError: (e: any) => toast.error(e.message),
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        target: form.target.trim(),
        serverIds: allServers ? [] : serverIds,
        projectIds: allServers || allProjects ? [] : projectIds,
        allProjects: !allServers && allProjects,
      };
      return editing ? api.patch<AlertRecipient>(`/monitoring/recipients/${id}`, body) : api.post<AlertRecipient>('/monitoring/recipients', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['monitoring', 'recipients'] });
      navigate('/monitoring/alerts');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/monitoring/recipients/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['monitoring', 'recipients'] });
      toast.success(t('monitoring.alerts.removed'));
      navigate('/monitoring/alerts');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const isTelegram = form.channel === 'TELEGRAM';

  return (
    <>
      <FormPage
        title={editing ? `${t('common.edit')} — ${existing.data?.name ?? ''}` : t('monitoring.alerts.new')}
        backTo="/monitoring/alerts"
        backLabel={t('monitoring.alerts.title')}
        submitLabel={t('common.save')}
        submitting={save.isPending}
        disabled={!form.name.trim() || !form.target.trim() || coversNothing}
        loading={editing && existing.isLoading}
        error={existing.error}
        onRetry={() => void existing.refetch()}
        onSubmit={() => save.mutate()}
      >
        <FormGrid>
          <Field label={t('common.name')} required>
            <Input
              value={form.name}
              maxLength={120}
              placeholder={t('monitoring.alerts.namePlaceholder')}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
            />
          </Field>
          <Field label={t('monitoring.alerts.channel')} required>
            <Select
              value={form.channel}
              onChange={(e) => {
                setForm({ ...form, channel: e.target.value as AlertChannel });
                setChats(null);
              }}
            >
              <option value="TELEGRAM">{t('monitoring.channel.TELEGRAM')}</option>
              <option value="EMAIL">{t('monitoring.channel.EMAIL')}</option>
            </Select>
          </Field>

          <Field
            label={isTelegram ? t('monitoring.alerts.chatId') : t('common.email')}
            required
            hint={isTelegram ? t('monitoring.alerts.chatIdHint') : undefined}
            className="sm:col-span-2"
          >
            <div className="flex gap-2">
              <Input
                dir="ltr"
                type={isTelegram ? 'text' : 'email'}
                value={form.target}
                maxLength={254}
                placeholder={isTelegram ? '-1001234567890' : 'ops@viganium.com'}
                onChange={(e) => setForm({ ...form, target: e.target.value })}
              />
              {isTelegram && (
                <Button icon={<Search className="h-4 w-4" />} loading={findChats.isPending} onClick={() => findChats.mutate()}>
                  {t('monitoring.alerts.findChats')}
                </Button>
              )}
            </div>
          </Field>

          {isTelegram && chats && (
            <div className="rounded-md border hairline p-3 sm:col-span-2">
              {chats.length === 0 ? (
                <p className="text-body-sm text-muted">{t('monitoring.alerts.noChats')}</p>
              ) : (
                <ul className="space-y-1">
                  {chats.map((chat) => (
                    <li key={chat.id}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-start text-body-sm hover:bg-[var(--surface-sunken)]"
                        onClick={() => setForm({ ...form, target: chat.id, name: form.name || chat.title })}
                      >
                        <span>{chat.title}</span>
                        <span dir="ltr" className="font-mono text-mono-sm text-muted">{chat.id}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <Field label={t('monitoring.alerts.language')}>
            <Select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value as 'en' | 'ar' })}>
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </Select>
          </Field>
          <Field label={t('monitoring.alerts.minSeverity')} hint={t('monitoring.alerts.minSeverityHint')}>
            <Select value={form.minSeverity} onChange={(e) => setForm({ ...form, minSeverity: e.target.value as MonitorSeverity })}>
              {MONITOR_SEVERITIES.map((s) => (
                <option key={s} value={s}>{t(severityLabel(s))}</option>
              ))}
            </Select>
          </Field>

          <div className="flex flex-col gap-2 sm:col-span-2">
            <Checkbox
              checked={form.notifyRecovery}
              onChange={(e) => setForm({ ...form, notifyRecovery: e.target.checked })}
              label={t('monitoring.alerts.notifyRecovery')}
            />
            <Checkbox
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              label={t('monitoring.alerts.isActive')}
            />
          </div>

          <div className="mt-2 border-t hairline pt-4 sm:col-span-2">
            <p className="text-body-sm font-medium text-[var(--text-secondary)]">{t('monitoring.alerts.covers')}</p>
            <p className="mb-3 text-caption text-faint">{t('monitoring.alerts.coversHint')}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <CoverList
                title={t('monitoring.servers.title')}
                allLabel={t('monitoring.alerts.allServers')}
                all={allServers}
                onAll={setAllServers}
                items={(servers.data ?? []).map((s) => ({ id: s.id, label: s.name }))}
                picked={serverIds}
                onToggle={(value) => toggle(serverIds, setServerIds, value)}
              />
              <CoverList
                title={t('nav.projects')}
                allLabel={t('monitoring.alerts.allProjects')}
                all={allProjects}
                onAll={setAllProjects}
                items={(projects.data ?? []).map((p) => ({ id: p.id, label: `${p.code} · ${localized(p)}` }))}
                picked={projectIds}
                onToggle={(value) => toggle(projectIds, setProjectIds, value)}
                shutNote={allServers ? t('monitoring.alerts.allServersNote') : undefined}
              />
            </div>
            {coversNothing && (
              <p className="mt-3 text-caption text-warning-fg dark:text-warning-dark">{t('monitoring.alerts.coversNothing')}</p>
            )}
          </div>
        </FormGrid>
      </FormPage>

      {editing && (
        <div className="mt-4 max-w-3xl">
          <Button variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmRemove(true)}>
            {t('monitoring.alerts.remove')}
          </Button>
        </div>
      )}

      {confirmRemove && (
        <ConfirmDialog
          title={t('monitoring.alerts.removeConfirm', { name: existing.data?.name ?? '' })}
          confirmLabel={t('monitoring.alerts.remove')}
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmRemove(false)}
        >
          <p>{t('monitoring.alerts.removeBody')}</p>
        </ConfirmDialog>
      )}
    </>
  );
}

/**
 * One of Covers' two lists: "All" on top, then each one to pick. With "All"
 * ticked every row shows ticked and none can be changed. `shutNote` closes the
 * whole list, saying why; what was picked in it comes back when it opens again.
 */
function CoverList({
  title,
  allLabel,
  all,
  onAll,
  items,
  picked,
  onToggle,
  shutNote,
}: {
  title: string;
  allLabel: string;
  all: boolean;
  onAll: (next: boolean) => void;
  items: Array<{ id: string; label: ReactNode }>;
  picked: string[];
  onToggle: (id: string) => void;
  shutNote?: string;
}) {
  const shut = !!shutNote;
  return (
    <div>
      <p className="mb-1.5 text-caption uppercase text-[var(--text-tertiary)]">{title}</p>
      <div className={clsx('rounded-md border hairline', shut && 'opacity-60')}>
        <div className="border-b hairline px-2 py-2">
          <Checkbox
            checked={all}
            disabled={shut}
            onChange={(e) => onAll(e.target.checked)}
            label={<span className="font-medium text-[var(--text-primary)]">{allLabel}</span>}
          />
        </div>
        <ul className="max-h-60 space-y-1 overflow-y-auto p-2">
          {items.map((item) => (
            <li key={item.id}>
              <Checkbox
                checked={all || picked.includes(item.id)}
                disabled={all || shut}
                onChange={() => onToggle(item.id)}
                label={item.label}
              />
            </li>
          ))}
          {items.length === 0 && <li className="text-body-sm text-muted">—</li>}
        </ul>
      </div>
      {shutNote && <p className="mt-1.5 text-caption text-faint">{shutNote}</p>}
    </div>
  );
}
